/**
 * PayoffChart — P&L at expiry for a multi-leg option position.
 *
 * One series, so no legend: the fill flips green above zero and red below,
 * with reference lines for the current spot and each breakeven.
 */

import { useId, useMemo } from 'react';
import { Area, CartesianGrid, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CHART, CHART_AXIS, CHART_TOOLTIP } from '@/lib/chart';
import { formatINR, formatNumber } from '@/lib/format';
import type { PayoffPoint } from '@/lib/options';

interface PayoffChartProps {
  curve: PayoffPoint[];
  spot?: number | null;
  breakevens?: number[];
  height?: number;
}

export function PayoffChart({ curve, spot, breakevens = [], height = 220 }: PayoffChartProps) {
  const gradientId = useId();

  // Offset where the gradient crosses zero, so profit and loss get their own colour.
  const zeroOffset = useMemo(() => {
    if (curve.length === 0) return 0.5;
    const max = Math.max(...curve.map((p) => p.pnl));
    const min = Math.min(...curve.map((p) => p.pnl));
    if (max <= 0) return 0;
    if (min >= 0) return 1;
    return max / (max - min);
  }, [curve]);

  if (curve.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-line text-xs text-fg-subtle">
        Add legs from the chain to see the payoff.
      </div>
    );
  }

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={curve} margin={{ top: 8, right: 8, bottom: 0, left: -4 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset={0} stopColor={CHART.profit} stopOpacity={0.35} />
              <stop offset={zeroOffset} stopColor={CHART.profit} stopOpacity={0.04} />
              <stop offset={zeroOffset} stopColor={CHART.loss} stopOpacity={0.04} />
              <stop offset={1} stopColor={CHART.loss} stopOpacity={0.35} />
            </linearGradient>
            <linearGradient id={`${gradientId}-stroke`} x1="0" y1="0" x2="0" y2="1">
              <stop offset={zeroOffset} stopColor={CHART.profit} />
              <stop offset={zeroOffset} stopColor={CHART.loss} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          <XAxis
            dataKey="price"
            type="number"
            domain={['dataMin', 'dataMax']}
            {...CHART_AXIS}
            tickFormatter={(v: number) => formatNumber(v)}
            tickCount={7}
          />
          <YAxis {...CHART_AXIS} width={64} tickFormatter={(v: number) => `${v < 0 ? '-' : ''}₹${Math.abs(v / 1000).toFixed(0)}k`} />
          <Tooltip
            contentStyle={CHART_TOOLTIP}
            labelFormatter={(v) => `Expiry at ${formatNumber(v as number)}`}
            formatter={(value: unknown) => [formatINR(value as number), 'P&L']}
          />
          <ReferenceLine y={0} stroke={CHART.axis} strokeWidth={1} />
          {spot && spot > 0 && (
            <ReferenceLine
              x={spot}
              stroke={CHART.info}
              strokeDasharray="4 3"
              label={{ value: 'Spot', fill: CHART.info, fontSize: 10, position: 'insideTopRight' }}
            />
          )}
          {breakevens.map((be) => (
            <ReferenceLine
              key={be}
              x={be}
              stroke={CHART.warning}
              strokeDasharray="2 3"
              label={{ value: formatNumber(be), fill: CHART.warning, fontSize: 10, position: 'insideBottomLeft' }}
            />
          ))}
          <Area
            type="linear"
            dataKey="pnl"
            stroke={`url(#${gradientId}-stroke)`}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            baseValue={0}
            isAnimationActive={false}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
